import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Button,
  Modal,
  message,
  Divider,
  Row,
  Col,
  Space,
  Input,
  DatePicker,
  Alert,
  Radio,
  Rate,
  InputNumber,
  Checkbox,
} from "antd";
import { Api_Url } from "../../../config/config";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;

// --- RATING CONSTANTS ---
const RATING_OPTIONS = ["Poor", "Average", "Good", "Excellent"];
const RATING_LABELS = { 1: "Poor", 2: "Average", 3: "Good", 4: "Excellent" };
const RATING_VALUES = { Poor: 1, Average: 2, Good: 3, Excellent: 4 };
const OTP_COOLDOWN_SECONDS = 60; // Define cooldown period

// Cheque Handover Mode Options (Must match backend validation exactly)
const HANDOVER_MODES = [
  "Customer Visited Branch",
  "Process through NEFT/RTGS",
  "Cheque handover to Lawyer",
];

// Helper functions (kept for reference, but no longer strictly needed in RatingInput)
const rateFormatter = (value) => RATING_LABELS[value] || null;
const rateParser = (value) => RATING_VALUES[value] || 1;

/**
 * MODIFIED: Replaced Rate component with Ant Design Select dropdown.
 */
const RatingInput = ({ name, label }) => (
  <Form.Item
    name={name}
    label={<span style={{ fontWeight: 500 }}>{label}</span>}
    rules={[{ required: true, message: `Please select rating for ${label}` }]}
  >
    <Select
      placeholder="Select Rating (1=Poor, 4=Excellent)"
      size="large"
      style={{ width: "100%" }}
    >
      {/* Map through the string options directly */}
      {RATING_OPTIONS.map((option) => (
        <Option key={option} value={option}>
          {option} ({RATING_VALUES[option]})
        </Option>
      ))}
    </Select>
  </Form.Item>
);

/**
 * Helper function to format date/time objects into ISO string for the API.
 */
const formatToISO = (dateValue) => {
  if (dateValue && dateValue.toDate) {
    return dateValue.toDate().toISOString();
  }
  if (dateValue instanceof Date) {
    return dateValue.toISOString();
  }
  return "";
};

/**
 * Helper function to format date objects into YYYY-MM-DD string for the API.
 */
const formatToDateString = (dateValue) => {
  if (dateValue && dateValue.format) {
    return dateValue.format("YYYY-MM-DD");
  }
  return "";
};

export const CustomerFeedbackModalAdd = ({ onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [location, setLocation] = useState({ lat: null, lng: null });

  const [searching, setSearching] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [customerSearchMode, setCustomerSearchMode] = useState(true);
  const [searchBy, setSearchBy] = useState("applicationId");
  const [customerList, setCustomerList] = useState([]); // Store all matching customers

  // State for OTP flow simulation (Client side only)
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  // FIX: State to hold the encrypted verification code from the Send API response.data
  const [sentVerificationCode, setSentVerificationCode] = useState(null);

  // Initial rating value for form state should be the API string ("Poor")
  const initialRatingString = RATING_LABELS[1]; // "Poor"

  const resetOTPState = (newMobileNumber) => {
    setIsOTPSent(false);
    setOtpVerified(false);
    setSecondsRemaining(0);
    setSentVerificationCode(null); // Reset the verification code
    form.setFieldsValue({
      OtpCode: null,
      CustomerDigitalConsentToken: null,
      BranchManagerConsentStatus: false,
    });

    if (!newMobileNumber) {
      setCustomerSearchMode(true);
      setCustomerDetails(null);
      setSearchAttempted(false);
    }
  };

  // --- Timer Effect ---
  useEffect(() => {
    let timerId;
    if (secondsRemaining > 0) {
      timerId = setInterval(() => {
        setSecondsRemaining((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [secondsRemaining]);
  // --- END Timer Effect ---

  useEffect(() => {
    fetchBranches();
    getGeolocation();

    // Auto-set the role
    try {
      const userRole = JSON.parse(sessionStorage.getItem("role"));
      if (userRole === "Sales") {
        form.setFieldsValue({ SubmittedBySalesOROperations: "Sales" });
      } else if (userRole === "Operation") {
        form.setFieldsValue({ SubmittedBySalesOROperations: "Operations" });
      }
    } catch (error) {
      console.error("Could not parse user role from session storage:", error);
      message.error("Could not determine user role from session.");
    }
  }, [form]);

  // FIX: Implement helper to reset specific fields for conditional rendering logic
  const resetConditionalFields = () => {
    form.resetFields([
      "DateOfCustomerVisit",
      "CustomerArrivalTime",
      "InteractionStartTime",
      "CustomerOutTime",
      "AttendingEmployeeName",
      "AttendingEmployeeCode",
      "AreaOfImprovement",
      "BranchOperationsRemarks",
      "BranchSalesTeamRemarks",
      "CustomerMobileNumber",
      "OtpCode",
      "CustomerDigitalConsentToken",
      "BranchManagerConsentStatus",
    ]);

    // Re-apply initial rating values as the resetFields above may clear them
    form.setFieldsValue({
      FeedbackServiceRating: initialRatingString,
      CourtesyBehaviourRating: initialRatingString,
      StaffKnowledgeRating: initialRatingString,
      WaitingTimeRating: initialRatingString,
      QualityOfHandlingRating: initialRatingString,
      OverallSatisfactionRating: initialRatingString,
      OverallExperienceRating: initialRatingString,
    });
    resetOTPState();
    setCustomerList([]); // Clear customer list when resetting fields
  };

  const fetchBranches = async () => {
    try {
      const response = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const data = await response.json();
      // FIX: Ensure data is an array before setting
      setBranches(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      message.error("Failed to load branches");
    }
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        // IMPROVEMENT: Handle error case gracefully in console
        (error) => {
          console.warn("Geolocation denied or unavailable:", error.message);
        }
      );
    }
  };

  const searchCustomer = async () => {
    const branchId = form.getFieldValue("BranchId");
    const searchValue = form.getFieldValue("ApplicationIdSearch");

    if (!branchId) {
      message.warning("Please select a branch first.");
      return;
    }
    if (!searchValue) {
      message.warning("Please enter a value to search.");
      return;
    }

    setSearchAttempted(true);
    setSearching(true);
    setCustomerDetails(null);
    setCustomerList([]);
    resetOTPState(); // Reset OTP on new customer search

    let apiUrl = "";
    const token = JSON.parse(sessionStorage.getItem("user")).accessToken;

    try {
      if (searchBy === "applicationId") {
        apiUrl = `${Api_Url}api/v1/Customer/bybranchidandname/${branchId}/${encodeURIComponent(
          searchValue
        )}?loanNumber=`;
      } else {
        apiUrl = `${Api_Url}api/v1/Customer/bybranchidandnameandloannumber/${branchId}/_/?loanNumber=${encodeURIComponent(
          searchValue
        )}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (response.ok && Array.isArray(result.data) && result.data.length > 0) {
        // Store all customers and show them in the dropdown
        setCustomerList(result.data);
        form.setFieldsValue({
          ApplicationId: result.data[0].applicationNo,
          CustomerName: undefined,
          CustomerMobileNumber: "",
        });
        setCustomerSearchMode(false);
        message.success(
          result.data.length === 1
            ? "Customer found. Please select from the dropdown."
            : `${result.data.length} customers found. Please select the correct customer.`
        );
      } else {
        setCustomerDetails(null);
        setCustomerList([]);
        message.info(
          `No customer found for the specified ${
            searchBy === "applicationId" ? "Application ID" : "Loan Number"
          }.`
        );
      }
    } catch (error) {
      message.error("Failed to search for customer.");
      setCustomerDetails(null);
      setCustomerList([]);
    } finally {
      setSearching(false);
    }
  };

  const handleCustomerFound = (customer) => {
    setCustomerDetails(customer);
    form.setFieldsValue({
      ApplicationId: customer.applicationNo,
      CustomerName: customer.name,
      CustomerMobileNumber: customer.mobileNumber || "",
    });
    message.success("Customer selected successfully.");
  };

  const enableManualEntry = () => {
    const searchValue = form.getFieldValue("ApplicationIdSearch");

    if (searchBy === "applicationId") {
      form.setFieldsValue({
        ApplicationId: searchValue,
        CustomerName: "",
        CustomerMobileNumber: "",
      });
    } else {
      form.setFieldsValue({
        ApplicationId: "",
        CustomerName: "",
        CustomerMobileNumber: "",
      });
    }

    resetOTPState();
    setCustomerSearchMode(false);
    setCustomerDetails(null);
    setCustomerList([]);
    setSearchAttempted(false);
  };

  // --- NEW: OTP Functionality ---
  const handleSendOtp = async () => {
    const mobile = form.getFieldValue("CustomerMobileNumber");
    const mobileString = String(mobile);
    if (!mobile || mobileString.length !== 10) {
      message.error("Please enter a valid 10-digit mobile number first.");
      return;
    }

    // Check if cooldown is active
    if (secondsRemaining > 0) {
      message.warning(
        `Please wait ${secondsRemaining} seconds before trying again.`
      );
      return;
    }

    setOtpLoading(true);
    try {
      const response = await fetch(`${Api_Url}api/Otp/send/${mobileString}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send OTP.");
      }

      // FIX 1: Capture the encrypted token from the response data field
      const result = await response.json();
      if (result.data) {
        setSentVerificationCode(result.data);
      }

      message.success("OTP sent successfully! Please enter the code below.");
      setIsOTPSent(true);
      // Start the cooldown timer
      setSecondsRemaining(OTP_COOLDOWN_SECONDS);
      // FIX: Ensure OTP field is cleared on send, so the user has to re-enter it.
      form.setFieldsValue({ OtpCode: null });
    } catch (error) {
      message.error(error.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const mobile = form.getFieldValue("CustomerMobileNumber");
    const otpCode = form.getFieldValue("OtpCode");

    if (!mobile || !otpCode) {
      message.error(
        "Mobile number and OTP code are required for verification."
      );
      return;
    }

    if (!sentVerificationCode) {
      message.error("Please send OTP first to receive the verification token.");
      return;
    }

    const codeString = String(otpCode);
    if (codeString.length !== 6) {
      message.error("OTP must be a 6-digit code.");
      return;
    }

    setOtpLoading(true);

    try {
      // FIX 2: Send the captured encrypted token in the 'code' field.
      // The 6-digit number is sent in the 'otp' field.
      const requestBody = {
        Mobile: String(mobile),
        Code: sentVerificationCode, // Use the encrypted token from the send response
        Otp: codeString, // Use the 6-digit code entered by the user
      };

      const response = await fetch(`${Api_Url}api/Otp/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "OTP verification failed. Please try again."
        );
      }

      // Check if the response body is empty before calling .json()
      const contentType = response.headers.get("content-type");
      let result = {};
      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        message.warning(
          "OTP verified successfully, but API returned no content. Auto-generating consent token."
        );
      }

      // Get the actual verification token from API response if available
      let verificationToken;

      // The server returns the plain OTP in the data field on success (result.data: "097049")
      // We will use the *encrypted token* as the final digital consent token if the API doesn't return a better one.
      if (result.data && result.succeeded) {
        // If verification succeeded, the encrypted code (sentVerificationCode) serves as the proof of consent token
        verificationToken = sentVerificationCode;
      }
      // Fallback/Safety Check: If the API gave an explicit token in result.token, use it.
      if (result.token) {
        verificationToken = result.token;
      }

      // If we still don't have a token (shouldn't happen if API succeeds), generate a generic one.
      if (!verificationToken) {
        const tokenString = `OTP_VERIFIED_${Date.now()}_${mobile}`;
        verificationToken = btoa(tokenString)
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=/g, "");
      }

      form.setFieldsValue({ CustomerDigitalConsentToken: verificationToken });
      message.success("OTP successfully verified! Digital consent secured.");
      setOtpVerified(true);
      setIsOTPSent(false);
      setSecondsRemaining(0); // Stop the timer on successful verification
    } catch (error) {
      console.error(error);
      message.error(error.message);
      setOtpVerified(false);
      form.setFieldsValue({ CustomerDigitalConsentToken: null });
    } finally {
      setOtpLoading(false);
    }
  };
  // --- END: OTP Functionality ---

  const onFinish = async (values) => {
    if (customerSearchMode) {
      message.error(
        "Please search for a customer or select 'Enter Manually' before submitting."
      );
      return;
    }

    const handoverMode = values.ChequeHandoverMode;
    // Determine if feedback is mandatory based on Handover Mode
    const feedbackMandatory = handoverMode === "Customer Visited Branch";

    // --- Validation Checks ---
    if (feedbackMandatory) {
      if (!otpVerified) {
        message.error(
          "Digital Consent (OTP/e-Signature) is mandatory for 'Customer Visited Branch' mode. Please verify OTP."
        );
        return;
      }

      if (!values.BranchManagerConsentStatus) {
        message.error(
          "Branch Manager Consent is mandatory for 'Customer Visited Branch' mode."
        );
        return;
      }

      // Check date/time fields which are only mandatory in this mode
      if (
        !values.DateOfCustomerVisit ||
        !values.CustomerArrivalTime ||
        !values.InteractionStartTime ||
        !values.CustomerOutTime ||
        !values.AttendingEmployeeCode ||
        !values.AttendingEmployeeName
      ) {
        message.error(
          "All Date/Time and Employee fields are mandatory for 'Customer Visited Branch' mode."
        );
        // This is a redundant check if Antd rules are set, but provides a safety net.
        return;
      }
    }
    // --- End Validation Checks ---

    setLoading(true);
    const formData = new FormData();

    formData.append("BranchId", values.BranchId);
    formData.append("ApplicationId", values.ApplicationId);
    formData.append("CustomerName", values.CustomerName);
    formData.append(
      "ChequeHandoverDate",
      formatToDateString(values.ChequeHandoverDate)
    );

    // NEW FIELDS
    formData.append("ChequeHandoverMode", handoverMode);
    // Use || "" for all non-required string fields to ensure they are sent
    formData.append(
      "AttendingEmployeeName",
      values.AttendingEmployeeName || ""
    );
    formData.append(
      "AttendingEmployeeCode",
      values.AttendingEmployeeCode || ""
    );

    // FIX: Ensure lat/lng are sent as numbers or strings of numbers
    formData.append(
      "Latitude",
      location.lat !== null ? String(location.lat) : ""
    );
    formData.append(
      "Longitude",
      location.lng !== null ? String(location.lng) : ""
    );
    formData.append(
      "SubmittedBySalesOROperations",
      values.SubmittedBySalesOROperations
    );

    // Digital Consent Fields
    formData.append(
      "CustomerMobileNumber",
      String(values.CustomerMobileNumber || "")
    );
    formData.append(
      "CustomerDigitalConsentToken",
      values.CustomerDigitalConsentToken || ""
    );

    // FIX: Checkbox values are boolean. Must send 'true'/'false' or equivalent if API expects string/number.
    // Sending boolean/string 'true'/'false' is safer for FormData.
    formData.append(
      "BranchManagerConsentStatus",
      values.BranchManagerConsentStatus ? "true" : "false"
    );

    // Use the helper to format date to ISO string for the BM consent timestamp
    formData.append(
      "BranchManagerConsentDateTime",
      values.BranchManagerConsentStatus ? formatToISO(new Date()) : "" // Use new Date() to get current submission time
    );

    // Conditional fields for 'Customer Visited Branch' mode
    if (feedbackMandatory) {
      formData.append(
        "DateOfCustomerVisit",
        formatToDateString(values.DateOfCustomerVisit)
      );
      // NOTE: Timepickers use moment objects, formatToISO handles them.
      formData.append(
        "CustomerArrivalTime",
        formatToISO(values.CustomerArrivalTime)
      );
      formData.append(
        "InteractionStartTime",
        formatToISO(values.InteractionStartTime)
      );
      formData.append("CustomerOutTime", formatToISO(values.CustomerOutTime));

      // Detailed Ratings (Already formatted to API string via getValueFromEvent on RatingInput)
      formData.append(
        "FeedbackServiceRating",
        values.FeedbackServiceRating || ""
      );
      formData.append(
        "CourtesyBehaviourRating",
        values.CourtesyBehaviourRating || ""
      );
      formData.append(
        "StaffKnowledgeRating",
        values.StaffKnowledgeRating || ""
      );
      formData.append("WaitingTimeRating", values.WaitingTimeRating || "");
      formData.append(
        "QualityOfHandlingRating",
        values.QualityOfHandlingRating || ""
      );
      formData.append(
        "OverallSatisfactionRating",
        values.OverallSatisfactionRating || ""
      );
      formData.append(
        "OverallExperienceRating",
        values.OverallExperienceRating || ""
      );

      // Remarks
      formData.append("AreaOfImprovement", values.AreaOfImprovement || "");
      formData.append(
        "BranchOperationsRemarks",
        values.BranchOperationsRemarks || ""
      );
      formData.append(
        "BranchSalesTeamRemarks",
        values.BranchSalesTeamRemarks || ""
      );
    }

    try {
      const response = await fetch(`${Api_Url}api/v1/CustomerFeedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Check if the error is from the API command handler
        if (errorData.errors && errorData.errors.length > 0) {
          throw new Error(errorData.errors.join("; ") || "Submission failed");
        }
        throw new Error(errorData.message || "Submission failed");
      }

      message.success("Customer feedback submitted successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "20px", fontWeight: "bold" }}>
          Add Customer Feedback
        </span>
      }
      width={1000}
      open={true} // FIX: Antd v4/v5 uses 'open' instead of 'visible'
      onCancel={onClose}
      footer={[
        <Space key="footer-buttons" size="large">
          <Button
            key="back"
            onClick={onClose}
            size="large"
            style={{ width: 120 }}
          >
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            size="large"
            style={{ width: 120 }}
            disabled={customerSearchMode}
          >
            Submit
          </Button>
        </Space>,
      ]}
      centered
      bodyStyle={{ padding: "24px" }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          // FIX: Initial values for Rate component fields must be the API string ("Poor"),
          // which the RatingInput component now correctly converts to 1 (number) for display.
          FeedbackServiceRating: RATING_LABELS[1],
          CourtesyBehaviourRating: RATING_LABELS[1],
          StaffKnowledgeRating: RATING_LABELS[1],
          WaitingTimeRating: RATING_LABELS[1],
          QualityOfHandlingRating: RATING_LABELS[1],
          OverallSatisfactionRating: RATING_LABELS[1],
          OverallExperienceRating: RATING_LABELS[1],
          CustomerArrivalTime: moment(),
          InteractionStartTime: moment(),
          CustomerOutTime: moment(),
        }}
      >
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              name="BranchId"
              label={<span style={{ fontWeight: 500 }}>Branch</span>}
              rules={[{ required: true, message: "Please select a branch" }]}
            >
              <Select
                placeholder="Select branch to begin"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                size="large"
                onChange={() => {
                  setCustomerSearchMode(true);
                  setSearchAttempted(false);
                  setCustomerDetails(null);
                  resetConditionalFields(); // FIX: Reset all conditional fields
                  form.resetFields([
                    "ApplicationIdSearch",
                    "ApplicationId",
                    "CustomerName",
                  ]); // Keep ChequeHandoverMode to allow immediate re-selection if needed
                }}
              >
                {branches.map((branch) => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="SubmittedBySalesOROperations"
              label={<span style={{ fontWeight: 500 }}>Submitting As</span>}
              rules={[
                {
                  required: true,
                  message: "Your role could not be determined",
                },
              ]}
            >
              <Select
                placeholder="Your role will be auto-detected"
                size="large"
                disabled={true}
              >
                <Option value="Sales">Sales</Option>
                <Option value="Operations">Operations</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            {/* --- NEW FIELD: Cheque Handover Mode --- */}
            <Form.Item
              name="ChequeHandoverMode"
              label={
                <span style={{ fontWeight: 500 }}>Cheque Handover Mode</span>
              }
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select
                placeholder="Select mode"
                size="large"
                // FIX: Reset conditional fields when mode changes
                onChange={() => resetConditionalFields()}
              >
                {HANDOVER_MODES.map((mode) => (
                  <Option key={mode} value={mode}>
                    {mode}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Customer Details</Divider>

        {/* Search/Manual Entry Logic */}
        {customerSearchMode ? (
          <>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={<span style={{ fontWeight: 500 }}>Search By</span>}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      setSearchAttempted(false); // Reset search attempt on change
                      setCustomerDetails(null);
                      form.setFieldsValue({ ApplicationIdSearch: null });
                    }}
                    value={searchBy}
                  >
                    <Radio.Button value="applicationId">
                      Application ID
                    </Radio.Button>
                    <Radio.Button value="loanNumber">Loan Number</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="ApplicationIdSearch"
                  label={
                    <span style={{ fontWeight: 500 }}>
                      {searchBy === "applicationId"
                        ? "Search by Application ID"
                        : "Search by Loan Number"}
                    </span>
                  }
                  // No rules here, validation is handled in searchCustomer
                >
                  <Input
                    placeholder={`Enter ${
                      searchBy === "applicationId"
                        ? "Application ID"
                        : "Loan Number"
                    } and press Enter or click search`}
                    size="large"
                    suffix={
                      <Button
                        // FIX: Use Antd default button props, color/variant are not standard props
                        type="default"
                        icon={<i className="fa-solid fa-magnifying-glass"></i>} // FIX: className instead of class
                        onClick={searchCustomer}
                        loading={searching}
                      />
                    }
                    onPressEnter={searchCustomer}
                  />
                </Form.Item>
              </Col>
            </Row>
            {searchAttempted && !searching && !customerDetails && customerList.length === 0 && (
              <Alert
                message="Customer Not Found"
                description={
                  <>
                    <p>No customer found. You can enter details manually.</p>
                    <Button type="primary" onClick={enableManualEntry}>
                      Enter Manually
                    </Button>
                  </>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </>
        ) : (
          <>
            <Row gutter={24}>
              <Col span={24}>
                <Button
                  type="dashed"
                  onClick={() => {
                    setCustomerSearchMode(true);
                    setCustomerDetails(null);
                    setCustomerList([]); // Clear customer list
                    setSearchAttempted(false);
                    // UX Improvement: Clear the main customer fields when going back to search
                    form.resetFields([
                      "ApplicationId",
                      "CustomerName",
                      "CustomerMobileNumber",
                    ]);
                    resetOTPState(); // Reset OTP as we are leaving the customer context
                  }}
                  style={{ marginBottom: 16 }}
                >
                  Search Again
                </Button>
              </Col>
            </Row>
            
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="ApplicationId"
                  label={
                    <span style={{ fontWeight: 500 }}>Application ID</span>
                  }
                  rules={[
                    { required: true, message: "Application ID is required" },
                  ]}
                >
                  <Input
                    placeholder="Application ID"
                    size="large"
                    disabled={customerDetails !== null}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="CustomerName"
                  label={<span style={{ fontWeight: 500 }}>Customer Name</span>}
                  rules={[
                    { required: true, message: "Customer Name is required" },
                  ]}
                >
                  {customerList.length > 0 ? (
                    <Select
                      placeholder="Select customer name"
                      size="large"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        String(option.children).toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={(selectedName) => {
                        const selected = customerList.find(c => c.name === selectedName);
                        if (selected) {
                          handleCustomerFound(selected);
                        }
                      }}
                    >
                      {customerList.map((customer) => (
                        <Option key={customer.id} value={customer.name}>
                          {customer.name}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      placeholder="Customer Name"
                      size="large"
                    />
                  )}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ChequeHandoverDate"
                  label={
                    <span style={{ fontWeight: 500 }}>
                      Cheque Handover Date
                    </span>
                  }
                  rules={[
                    { required: true, message: "This field is required" },
                  ]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        {/* End Search/Manual Entry Logic */}

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev.ChequeHandoverMode !== curr.ChequeHandoverMode
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("ChequeHandoverMode") ===
              "Customer Visited Branch" && (
              <>
                <Divider orientation="left">
                  In-Person Visit & Digital Feedback Details
                </Divider>

                <Row gutter={24}>
                  <Col span={6}>
                    <Form.Item
                      name="DateOfCustomerVisit"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Date of Customer Visit
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please select visit date" },
                      ]}
                    >
                      <DatePicker
                        format="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="CustomerArrivalTime"
                      label={
                        <span style={{ fontWeight: 500 }}>Arrival Time</span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please select arrival time",
                        },
                      ]}
                    >
                      <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="HH:mm"
                        picker="time"
                        style={{ width: "100%" }}
                        size="large"
                        placeholder="Select Time"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="InteractionStartTime"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Interaction Start Time
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please select start time" },
                      ]}
                    >
                      <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="HH:mm"
                        picker="time"
                        style={{ width: "100%" }}
                        size="large"
                        placeholder="Select Time"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="CustomerOutTime"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Interaction End Time
                        </span>
                      }
                      rules={[
                        { required: true, message: "Please select end time" },
                      ]}
                    >
                      <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="HH:mm"
                        picker="time"
                        style={{ width: "100%" }}
                        size="large"
                        placeholder="Select Time"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left" plain>
                  Employee Details
                </Divider>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      name="AttendingEmployeeName"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Attending Employee Name
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Employee Name is required",
                        },
                      ]}
                    >
                      <Input placeholder="Enter employee name" size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="AttendingEmployeeCode"
                      label={
                        <span style={{ fontWeight: 500 }}>Employee Code</span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Employee Code is required",
                        },
                      ]}
                    >
                      <Input placeholder="Enter employee code" size="large" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left" plain>
                  Customer Satisfaction Ratings (1=Poor, 4=Excellent)
                </Divider>
                <Row gutter={24}>
                  <Col span={12}>
                    <RatingInput
                      name="FeedbackServiceRating"
                      label="Feedback Service (Overall)"
                    />
                    <RatingInput
                      name="CourtesyBehaviourRating"
                      label="Courtesy & Behaviour of Branch Staff"
                    />
                    <RatingInput
                      name="StaffKnowledgeRating"
                      label="Staff Knowledge about Query/Problem"
                    />
                  </Col>
                  <Col span={12}>
                    <RatingInput
                      name="WaitingTimeRating"
                      label="Waiting Time before Service"
                    />
                    <RatingInput
                      name="QualityOfHandlingRating"
                      label="Quality of Handling Query/Problem"
                    />
                    <RatingInput
                      name="OverallSatisfactionRating"
                      label="Overall Satisfaction with Branch Services"
                    />
                    <RatingInput
                      name="OverallExperienceRating"
                      label="Overall Experience Rating"
                    />
                  </Col>
                </Row>

                <Divider orientation="left" plain>
                  Remarks & Improvement
                </Divider>
                <Row gutter={24}>
                  <Col span={8}>
                    <Form.Item
                      name="AreaOfImprovement"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Area of Improvement
                        </span>
                      }
                    >
                      <TextArea
                        rows={2}
                        placeholder="Enter area for improvement"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="BranchOperationsRemarks"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Branch Operations Remarks
                        </span>
                      }
                    >
                      <TextArea
                        rows={2}
                        placeholder="Enter operations remarks"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="BranchSalesTeamRemarks"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Branch Sales Team Remarks
                        </span>
                      }
                    >
                      <TextArea
                        rows={2}
                        placeholder="Enter sales team remarks"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left" plain>
                  Digital Consent (Mandatory)
                </Divider>
                <Row gutter={24} align="bottom">
                  <Col span={6}>
                    <Form.Item
                      name="CustomerMobileNumber"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Customer Mobile Number
                        </span>
                      }
                      rules={[
                        { required: true, message: "Mobile is required" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || String(value).length === 10) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("Mobile number must be 10 digits")
                            );
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        placeholder="Mobile No."
                        size="large"
                        style={{ width: "100%" }}
                        controls={false}
                        maxLength={10}
                        onChange={(value) => {
                          if (otpVerified || isOTPSent) {
                            resetOTPState(value);
                          }
                        }}
                        disabled={otpVerified}
                      />
                    </Form.Item>
                  </Col>

                  {/* Wrap Send OTP button in Form.Item with shouldUpdate */}
                  <Col span={6}>
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.CustomerMobileNumber !==
                        currentValues.CustomerMobileNumber
                      }
                      style={{ marginBottom: 0 }}
                    >
                      {({ getFieldValue }) => {
                        const mobile = getFieldValue("CustomerMobileNumber");
                        const isValidMobile =
                          mobile && String(mobile).length === 10;

                        // FIX: Use secondsRemaining for cooldown
                        const isCooldownActive = secondsRemaining > 0;
                        const isDisabled =
                          !isValidMobile ||
                          otpVerified ||
                          isCooldownActive ||
                          otpLoading;

                        let buttonText = "Send OTP";
                        if (otpVerified) {
                          buttonText = "Verified";
                        } else if (isCooldownActive) {
                          buttonText = `Resend (${secondsRemaining}s)`;
                        } else if (isOTPSent) {
                          buttonText = "Resend OTP";
                        }

                        return (
                          <Button
                            type="default"
                            size="large"
                            style={{ width: "100%", marginBottom: 24 }}
                            onClick={handleSendOtp}
                            loading={otpLoading}
                            disabled={isDisabled}
                          >
                            {buttonText}
                          </Button>
                        );
                      }}
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name="OtpCode"
                      label={<span style={{ fontWeight: 500 }}>Enter OTP</span>}
                      rules={[
                        {
                          required: isOTPSent || otpVerified,
                          message: "OTP is required",
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="6-digit code"
                        size="large"
                        style={{ width: "100%" }}
                        controls={false}
                        maxLength={6}
                        disabled={!isOTPSent || otpVerified}
                      />
                    </Form.Item>
                    <Form.Item name="CustomerDigitalConsentToken" hidden>
                      <Input />
                    </Form.Item>
                  </Col>

                  {/* Wrap Verify OTP button in Form.Item with shouldUpdate */}
                  <Col span={6}>
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.OtpCode !== currentValues.OtpCode
                      }
                      style={{ marginBottom: 0 }}
                    >
                      {({ getFieldValue }) => {
                        const otpCode = getFieldValue("OtpCode");
                        const isValidOtp =
                          otpCode && String(otpCode).length === 6;

                        return (
                          <Button
                            type="primary"
                            size="large"
                            style={{ width: "100%", marginBottom: 24 }}
                            onClick={handleVerifyOtp}
                            loading={otpLoading && isOTPSent}
                            disabled={!isOTPSent || !isValidOtp || otpVerified}
                          >
                            {otpVerified ? "Verified" : "Verify OTP"}
                          </Button>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={24}>
                    <Form.Item
                      name="BranchManagerConsentStatus"
                      valuePropName="checked"
                    >
                      <Checkbox disabled={!otpVerified}>
                        <span style={{ fontWeight: 500 }}>
                          Branch Manager Consent (Mandatory after Customer
                          Consent)
                        </span>
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                {(!otpVerified ||
                  !form.getFieldValue("BranchManagerConsentStatus")) && (
                  <Alert
                    message="Consent Required"
                    description="Customer OTP verification must be successful AND Branch Manager Consent must be checked before submission for this mode."
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
              </>
            )
          }
        </Form.Item>
      </Form>
    </Modal>
  );
};